import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { ReactComponent as NotificationLogo } from '../../styles/img/icons/error.svg';
import { scrollIntoView } from 'seamless-scroll-polyfill';
import { listenToAccessibilityKeyboardInputs } from '../../accessibleNavigation';
import { sendEmail } from '../../helpers/account';

export interface INotificationErrorMessage {
    message: string;
    messageSuffix?: string;
    messageEOL?: string;
    messageEOLId?: string;
    messagePrefix?: string;
    focusElement?: string;
    lowerCaseMessage?: boolean;
    email?: string;
    weight: number;
}

export interface INotificationProps extends WithTranslation {
    error: boolean;
    messages: INotificationErrorMessage[];
    label?: string;
}

const Notification = (props: INotificationProps) => {
    const { t, i18n, label } = props;

    const handleOnClickCorrect = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        const target = (e.target as HTMLAnchorElement).dataset.target;
        if (target) {
            const element = document.getElementById(target);
            document.getElementById(target)?.focus();
            scrollIntoView(element!, { behavior: 'auto', block: 'center', inline: 'center' });
        }
    };

    const handleOnClickSendLink = async (email: string) => {
        await sendEmail(i18n.language, email);
        alert(t('form_contact_send_again_link'))
    };

    const sortedMsg = props.messages.sort(
        (a: INotificationErrorMessage, b: INotificationErrorMessage) => {
            return a.weight - b.weight;
        }
    );
    let allErrorsLabel = '';
    const messageComponents = sortedMsg.map((element, index) => {
        const email = element.email
        const ariaMessage = (element.messagePrefix ? t(element.messagePrefix) : '')
            .concat(element.message ? t(element.message) : '')
            .concat(element.messageSuffix ? t(element.messageSuffix) : '')
        allErrorsLabel = allErrorsLabel.concat(ariaMessage.addSpace());
        if (element.focusElement === t('form_contact_error_email_focus_element') && email) {
            return (
                <div
                    key={element.message + element.focusElement}
                    className={props.error ? 'notification notification--error' : 'notification'}
                    aria-live="polite"
                >
                    <span>
                        {element.messagePrefix}
                        <a id={`error_element_${index}`}
                            onClick={() => handleOnClickSendLink(email)}
                            className="standalone-link"
                            tabIndex={0}
                            role="button"
                        >
                            {element.message}
                        </a>
                        {element.messageSuffix}
                    </span>
                </div>
            )
        } else {
            const message = (element.messagePrefix ? t(element.messagePrefix) : '')
                .concat(
                    element.lowerCaseMessage ? t(element.message).toLowerCase() : t(element.message)
                )
                .concat(element.messageSuffix ? t(element.messageSuffix!) : '')
                .concat(element.messageEOL ? t(element.messageEOL!) : '')
                .concat(element.messageEOLId ? t(element.messageEOLId!) : '');
            allErrorsLabel = allErrorsLabel.concat(message.addSpace());
            return (
                <div
                    key={element.message + element.focusElement}
                    className={props.error ? 'notification notification--error' : 'notification'}
                    aria-live="polite"
                >
                    {element.focusElement ? (
                        <a
                            id={`error_element_${index}`}
                            onClick={handleOnClickCorrect}
                            className="inline-link"
                            data-target={element.focusElement}
                            tabIndex={0}
                            aria-label={message}
                        >
                            {message}
                        </a>
                    ) : (
                        t(element.message)
                    )}
                </div>
            );
        }
    });

    if (props.error) {
        setTimeout(function () {
            messageComponents.forEach((message, index) => {
                listenToAccessibilityKeyboardInputs(
                    document.getElementById(`error_element_${index}`)
                );
            });
        }, 100);

        return (
            <div className="notification-container notification-container--error">
                <div className="notification-error-icon">
                    <NotificationLogo />
                </div>
                <div
                    tabIndex={0}
                    id="notification-error-list"
                    aria-label={allErrorsLabel}
                >
                    {messageComponents}
                </div>
            </div>
        );
    } else {
        return (
            <div
                className="notification-container"
                aria-label={label}
            >
                <div>{messageComponents}</div>
            </div>
        );
    }
};

export default withTranslation()(Notification);
